"use client";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SmartContactDisplay } from "@/components/shared/smart-display/SmartContactDisplay";
import { SmartUserDisplay } from "@/components/shared/smart-display/SmartUserDisplay";
import { useContactByEmail } from "@/lib/hooks/use-api";
import type { AppUser, Client } from "@/lib/types/database";

interface TaskAssigneeCardProps {
  userId: string;
  user: AppUser | null;
}

export function TaskAssigneeCard({ userId, user }: TaskAssigneeCardProps) {
  const email = user?.email?.trim() || "";
  const { data: crmContact, isLoading: crmLoading } = useContactByEmail(email);

  const directoryName = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim();
  const displayName =
    directoryName || crmContact?.name?.trim() || (email ? email.split("@")[0] : "") || userId;

  return (
    <Card>
      <CardHeader className="pb-2 border-b border-gray-100 bg-card-50">
        <CardTitle className="text-base flex flex-wrap items-center gap-3">
          <SmartUserDisplay
            userId={userId}
            userData={user || undefined}
            fallbackText={displayName}
            showIcon={false}
          />
          {user?.is_active === false ? (
            <Badge variant="secondary" className="text-xs">
              Inactive
            </Badge>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div className="sm:col-span-2">
            <dt className="text-gray-500">Name</dt>
            <dd className="text-card-foreground font-medium">{displayName}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Email</dt>
            <dd className="text-card-foreground font-medium break-all">
              {email || <span className="text-amber-800 dark:text-amber-200">No email on file — user id {userId}</span>}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-gray-500">CRM contact</dt>
            <dd className="text-card-foreground">
              {!email ? (
                "—"
              ) : crmLoading ? (
                <span className="text-gray-400">Loading…</span>
              ) : crmContact ? (
                <div className="space-y-2">
                  <SmartContactDisplay
                    contactId={crmContact.id}
                    contactData={crmContact as Client}
                    fallbackText={crmContact.name || crmContact.email || "Contact"}
                    showIcon={true}
                  />
                  {crmContact.company_name?.trim() ? (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Company:{" "}
                      <span className="text-card-foreground font-medium">
                        {crmContact.company_name.trim()}
                      </span>
                    </p>
                  ) : null}
                </div>
              ) : (
                "—"
              )}
            </dd>
          </div>
          {user ? (
            <>
              <div>
                <dt className="text-gray-500">Role</dt>
                <dd className="text-card-foreground">
                  {(user as AppUser & { roles?: { display_name?: string; name?: string } }).roles
                    ?.display_name ||
                    (user as AppUser & { roles?: { name?: string } }).roles?.name ||
                    user.role ||
                    "—"}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Account</dt>
                <dd className="text-card-foreground">{user.is_active ? "Active" : "Inactive"}</dd>
              </div>
            </>
          ) : email ? (
            <div className="sm:col-span-2 text-xs text-gray-500">
              Profile loaded by id; not in the admin directory list.
            </div>
          ) : (
            <div className="sm:col-span-2 text-sm text-amber-800 dark:text-amber-200">
              No Hub user profile matched this assignee id.
            </div>
          )}
        </dl>
      </CardContent>
    </Card>
  );
}
